import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, TextField } from "@mui/material";

import { ReactComponent as ChevronUp } from "@assets/icons/inbox/chevron-up.svg";

import { ReactComponent as ChevronDown } from "@assets/icons/inbox/chevron-down.svg";
import { useSearch } from "@/utils";
import { useLazyGeSuggestionQuery } from "@/pages/Private/redux/liPeople/liPeople.api";
import { SvgIcon } from "@/components/Icon/SvgIcon";

export interface LinkedinParams {
	name: string;
	id: string;
	image?: string;
	subtitle?: string;
}

interface LinkedinTagAutocompleteProps {
	value?: LinkedinParams[];
	labelText?: string;
	type: string;
	handleValueChange: (value: LinkedinParams[]) => void;
	placeholder?: string;
	multiple?: boolean;
}

export const LinkedinTagAutocomplete: FunctionComponent<LinkedinTagAutocompleteProps> = ({
	value,
	type,
	handleValueChange,
	placeholder,
	multiple,
	labelText,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPeople.${key}`);
	const { query, setSearchValue, searchValue } = useSearch();
	const [inputValue, setInputValue] = useState<string>();
	const [options, setOptions] = useState<LinkedinParams[]>([]);
	const [defaults, setDefaults] = useState<LinkedinParams[]>([]);
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [fetchTags, { isFetching }] = useLazyGeSuggestionQuery();

	useEffect(() => {
		setInputValue(query);
	}, [query, type]);

	useEffect(() => {
		if (value) {
			setDefaults(
				value?.map((value) => {
					return { name: value.name, id: value.id };
				}) || []
			);
		}
	}, [value]);

	useEffect(() => {
		if (inputValue) {
			fetchTags({ type, keyword: inputValue }).then((results) => {
				const optionValues = results?.data?.suggestions?.map((res) => {
					return {
						name: res.title,
						id: res.urn,
						image: res.image,
						subtitle: res.subtitle,
					};
				});

				if (optionValues) {
					setOptions([...optionValues]);
				}
			});
		}
	}, [inputValue, fetchTags, type]);

	return (value && defaults) || !value ? (
		<div className="flex flex-col w-full">
			<div className="text-sm font-medium text-gray-900 mb-2">
				{labelText || ts(`${type}.label`)}
			</div>
			<Autocomplete
				defaultValue={[]}
				filterOptions={(options) => {
					return options;
				}}
				getOptionLabel={(option) => option.name}
				id={type}
				loading={isFetching}
				loadingText={"Searching..."}
				multiple={multiple || true}
				noOptionsText={searchValue.length > 0 ? "Typing..." : "Start typing to search"}
				options={options}
				popupIcon={
					<SvgIcon className="w-[20px] h-[20px]" svgIcon={showDropdown ? ChevronUp : ChevronDown} />
				}
				renderInput={(params) => (
					<TextField
						sx={{
							width: "100%",
							minHeight: 48,
							marginBottom: 1,
							"& .MuiFilledInput-root": {
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
								borderRadius: 1.5,
								paddingTop: "0px",
								paddingBottom: "0px !important",
								paddingLeft: 1.5,
								"&.Mui-focused": {
									borderWidth: 1,
									boxShadow: "none",
								},
							},
						}}
						{...params}
						placeholder={placeholder}
					/>
				)}
				size="medium"
				sx={{
					width: "100%",
				}}
				value={defaults}
				onChange={(event, value) => {
					handleValueChange(value);
				}}
				onClose={() => {
					setShowDropdown(false);
				}}
				onInputChange={(event, newInputValue) => {
					if (event?.type === "change") {
						setOptions([]);
					}

					setSearchValue(newInputValue);
				}}
				onOpen={() => {
					setShowDropdown(true);
				}}
			/>
		</div>
	) : (
		<></>
	);
};
